import { action, observable, computed, runInAction } from 'mobx';
import { apiAuthCaller } from '@services/apiCaller';
import { IRootStore } from '@stores/index';
import { IEvent } from '@components/transformations/liveEvents';
import { IDestination, IDestinationStore } from './destination';

export interface ITransformationStore {
  id: string;
  name: string;
  description: string;
  code: string;
  codeVersion: string;
  destinationIds: string[];
  destinations: (IDestinationStore | undefined)[];
  isConnected: number;
  rootStore: IRootStore;
  update(tramsformation: ITransformationStore): void;
  connectToDestination(
    destinationId: string,
    onSuccess?: () => void,
    onError?: () => void,
    showAlert?: boolean,
  ): void;
  disconnectFromDestination(destinationId: string): void;
  delete(): void;
  getLiveEvents(latestId: number, from: number): Promise<IEvent[]>;
}

export class TransformationStore implements ITransformationStore {
  @observable public id: string;

  @observable public name: string;

  @observable public description: string;

  @observable public code: string;

  @observable public codeVersion: string;

  @observable public destinationIds: string[];

  @observable public rootStore: IRootStore;

  constructor(transformation: ITransformationStore, rootStore: IRootStore) {
    this.id = transformation.id;
    this.name = transformation.name;
    this.description = transformation.description;
    this.code = transformation.code;
    this.codeVersion = transformation.codeVersion;
    this.destinationIds = transformation.destinations.map(
      (d: IDestination | undefined) => d?.id || '',
    );
    this.rootStore = rootStore;
  }

  @action.bound
  public async update(transformation: ITransformationStore) {
    this.id = transformation.id;
    this.name = transformation.name;
    this.description = transformation.description;
    this.code = transformation.code;
    this.codeVersion = transformation.codeVersion;
  }

  @computed get destinations() {
    return this.destinationIds
      .map((destId: string) =>
        this.rootStore.destinationsListStore.destinations.find(
          (destination: IDestinationStore) => destination.id === destId,
        ),
      )
      .filter((element) => element !== undefined); // Filters out destinations that are soft deleted
    // TODO: Move filtering logic to config-backend
  }

  @computed get isConnected() {
    return this.destinations.length;
  }

  @action.bound
  public async connectToDestination(
    destinationId: string,
    onSuccess: () => void,
    onError: () => void,
    showAlert = false,
  ) {
    const {
      userStore,
      destinationsListStore,
      transformationsListStore,
      destinationDefsListStore,
      messagesStore,
    } = this.rootStore;
    try {
      const { token } = userStore;
      await apiAuthCaller(token).post(`/transformations/${this.id}/connectToDestination`, {
        destinationId,
      });
      if (showAlert) {
        messagesStore.showSuccessMessage('Transformation added to destination successfully');
      }
      onSuccess?.();
      runInAction(() => {
        // Removing all the connections between that destination with other transformations
        // This is to always have only one transformation per destination
        // We do a similar thing in backend too.
        // When we start supporting multiple transformations per destinations,
        // we remove next block of code and similar code from backend
        transformationsListStore.transformations.forEach((t) => {
          t.destinationIds = t.destinationIds.filter((destId) => destId !== destinationId);
        });

        if (!this.destinationIds.includes(destinationId)) {
          this.destinationIds = [...this.destinationIds, destinationId];
        }
      });
      const destDefId = destinationsListStore.destinations.find(
        (dest) => dest.id === destinationId,
      )!.destinationDefinition.id;
      const destType = destinationDefsListStore.destinationDefs.find(
        (destDef) => destDef.id === destDefId,
      )!.displayName;
      window.rudderanalytics.track('transformation used', {
        transformationId: this.id,
        destId: destinationId,
        destType,
      });
    } catch (error) {
      messagesStore.showErrorMessage('Failed to add transformation');
      onError?.();
    }
  }

  @action.bound
  public async delete() {
    try {
      const { token } = this.rootStore.userStore;
      await apiAuthCaller(token).delete(`/transformations/${this.id}`);
      runInAction(() => {
        this.rootStore.transformationsListStore.transformations =
          this.rootStore.transformationsListStore.transformations.filter(
            (t: ITransformationStore) => t.id !== this.id,
          );
      });
      window.rudderanalytics.track('transformation deleted', { transformationId: this.id });
    } catch (error) {
      window.rudderanalytics.track('transformation deletion failed', {
        transformationId: this.id,
        error,
      });
    }
  }

  @action.bound
  public async disconnectFromDestination(destinationId: string) {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    try {
      await apiAuthCaller(token).post(`/transformations/${this.id}/disconnectFromDestination`, {
        destinationId,
      });
      messagesStore.showSuccessMessage('Transformation removed successfully');
      runInAction(() => {
        this.destinationIds = this.destinationIds.filter((destId) => destId !== destinationId);
      });
    } catch (error) {
      messagesStore.showErrorMessage('Failed to remove transformation');
      window.rudderanalytics.track('transformation disconnection from destination failed', {
        transformationId: this.id,
        destId: destinationId,
        error,
      });
    }
  }

  public async getLiveEvents(latestId: number, timestamp: number) {
    const {
      userStore: { token },
    } = this.rootStore;
    const resp = await apiAuthCaller(token).get(
      `/eventTransformStatus?id=${latestId}&from=${timestamp}`,
      {
        headers: { 'X-TRANSFORMATION-ID': this.id },
      },
    );
    return resp.data;
  }
}
